// @file Manages state and logic for the whiteboard element modifier UI, which allows modifying properties of selected shapes
import device from '@@/bits/device'
import { awaitDOMUpdates } from '@@/bits/dom'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getPollFromPost, isPollClosed } from '@@/bits/surface_polls'
import {
  ActionType,
  getAllShapesInGroup,
  getCommonShapeProperty,
  getLongPressedShape,
  isGroupShape,
  isShapeApproved,
  isShapeChildOfGroup,
  shouldEnableGroupingForShapes,
} from '@@/bits/whiteboard'
import type { ConnectorStyle } from '@@/bits/whiteboard_element_modifier'
import {
  changeBackgroundRow,
  closePollRow,
  copyRow,
  cutRow,
  deleteRow,
  duplicateRow,
  editPollRow,
  exportPollRow,
  freezeRow,
  getFirstCharacterTextColorClass,
  groupRow,
  isShapeWithRichText,
  mapTldrawColorToWhiteboardColor,
  openPollRow,
  pasteRow,
  selectAllRow,
  sendToBackRow,
  sendToFrontRow,
  sharingAndPermissionsRow,
  TextFormattingOption,
  textFormattingOptions,
  THEME_FONT_MENU_ITEM_ID,
  unfreezeRow,
  ungroupRow,
} from '@@/bits/whiteboard_element_modifier'
import type {
  TldrawDefaultColorStyleColor,
  TldrawGeoShapeGeoStyle,
  ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { arrowToolColors, geoToolColors, getAllTools } from '@@/bits/whiteboard_tools'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import type { WhiteboardTextSize, WhiteboardTool } from '@@/types/whiteboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardAttachmentInteraction } from '@@/vuecomposables/use_whiteboard_attachment_interaction'
import { useWhiteboardHeadlessTiptapEditor } from '@@/vuecomposables/use_whiteboard_headless_tiptap_editor'
import { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { Content } from '@padlet/whiteboard-tiptap'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import {
  defaultTldrawOptions,
  type Box,
  type TLArrowShapeArrowheadStyle,
  type TLDefaultDashStyle,
  type TLDefaultFontStyle,
  type TLDefaultTextAlignStyle,
  type TLShape,
} from 'tldraw'
import { computed, ref, shallowRef } from 'vue'

export const useWhiteboardElementModifierStore = defineStore('whiteboardElementModifier', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardPanelsStore = useWhiteboardPanelsStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardLinkingStore = useWhiteboardLinkingStore()
  const whiteboardSpecialEffectsStore = useWhiteboardSpecialEffectsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const whiteboardUploadStore = useWhiteboardUploadStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const { shouldInteractWithShapeInPlayMode } = useWhiteboardAttachmentInteraction()

  const selectedShapes = shallowRef<TLShape[]>([])

  const onlySelectedShape = computed<TLShape | null>(() => {
    return selectedShapes.value.length === 1 ? selectedShapes.value[0] : null
  })

  useTldrawEditor().react((editor) => {
    selectedShapes.value = editor.getSelectedShapes()
  })

  // #region Context menu

  const contextMenuGroups = ref<OzMenuRowsRow[][]>([])
  const contextMenuAnchor = ref<Anchor>()
  const xContextMenu = ref(false)
  const contextMenuTriggeredByRightClick = ref(false)

  // Group shapes should always be allowed to be created
  const areSelectedShapesTypesAllowedToBeCreated = computed(() => {
    return selectedShapes.value.every(
      (shape) => shape.type === 'group' || whiteboardToolsStore.wasShapeCreatedFromAllowedTool(shape),
    )
  })

  const areAllSelectedShapesEditable = computed(() => {
    const editor = useTldrawEditor().editor
    if (editor == null) return false
    return selectedShapes.value.every(whiteboardPermissionsStore.canIEditShapeInFrame)
  })

  const linkRow = computed(() => {
    const hasLinking = whiteboardLinkingStore.checkIfLinkingExists(onlySelectedShape.value?.id ?? '')
    const linkingContextMenuText = hasLinking ? __('Edit link') : __('Add link')
    return {
      icon: 'link',
      text: linkingContextMenuText,
      eventPayload: ActionType.Link,
      rightLabel: '⇧L',
      shouldShow:
        onlySelectedShape.value != null &&
        onlySelectedShape.value?.type !== 'arrow' &&
        !whiteboardStore.isFrameBreakout &&
        !isShapeChildOfGroup(onlySelectedShape.value) &&
        !isOnlySelectedShapePoll.value &&
        !shouldInteractWithShapeInPlayMode(onlySelectedShape.value),
    }
  })

  const specialEffectsRow = computed(() => {
    const specialEffectsMenuText =
      onlySelectedShape.value == null
        ? ''
        : whiteboardSpecialEffectsStore.doesShapeHaveSpecialEffects(onlySelectedShape.value.id)
          ? __('Edit special effects')
          : __('Add special effects')
    return {
      icon: 'fx_squircle',
      text: specialEffectsMenuText,
      eventPayload: ActionType.SpecialEffect,
      shouldShow:
        selectedShapes.value.length === 1 &&
        onlySelectedShape.value != null &&
        whiteboardSpecialEffectsStore.canAddSpecialEffectForShape(onlySelectedShape.value),
    }
  })

  const isOnlySelectedShapePoll = computed(() => {
    return (
      onlySelectedShape.value != null &&
      onlySelectedShape.value.type === 'padlet-attachment-shape' &&
      (onlySelectedShape.value as PadletAttachmentShape).props.isPoll
    )
  })

  const isOnlySelectedShapePollClosed = computed(() => {
    if (!isOnlySelectedShapePoll.value) return false
    const postId = (onlySelectedShape.value as PadletAttachmentShape).props.postId
    const poll = getPollFromPost(surfacePostsStore.postEntitiesById[postId])
    return poll != null && isPollClosed(poll)
  })

  const sharingAndPermissionsRowComputed = computed<(OzMenuRowsRow & { shouldShow: boolean }) | null>(() => {
    const frameId = whiteboardStore.currentFrameId
    if (frameId == null) return null

    return {
      ...sharingAndPermissionsRow,
      text: whiteboardPermissionsStore.canIAdminister ? __('Sharing & permissions') : __('Sharing'),
      shouldShow: true,
    }
  })

  const changeBackgroundRowComputed = computed<(OzMenuRowsRow & { shouldShow: boolean }) | null>(() => {
    const frameId = whiteboardStore.currentFrameId
    if (frameId == null) return null

    const canEditFrame = whiteboardPermissionsStore.canIEditFrame(frameId)
    return {
      ...changeBackgroundRow,
      shouldShow: canEditFrame,
    }
  })

  function showContextMenu(): void {
    xContextMenu.value = true
  }

  function hideContextMenu(): void {
    xContextMenu.value = false
    contextMenuAnchor.value = undefined
    contextMenuTriggeredByRightClick.value = false
  }

  function buildContextMenu(
    e: PointerEvent | LongPressEvent,
    options: { source: 'right-click' | 'modifier-bar-click' } = { source: 'right-click' },
  ): void {
    // Only show context menu in draw mode
    if (!whiteboardModeStore.isDrawMode) return
    if (e instanceof LongPressEvent && !device.touchable) {
      // Only open context menu on long press for touch devices
      return
    }

    if (
      whiteboardStore.currentFrameId == null ||
      !whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId)
    ) {
      return
    }

    if (!isAppUsing('sandboxRetainActiveTool')) {
      const toolsThatAllowContextMenu = isAppUsing('removePlayMode') ? ['select', 'interact'] : ['select']
      if (!toolsThatAllowContextMenu.includes(whiteboardStore.selectedToolId)) {
        return
      }
    }

    if (whiteboardPanelsStore.xCardTemplatesPanel) {
      return
    }

    const pointerEventCoords = { x: e.x, y: e.y }

    // Track if context menu was triggered by right-click (not button click)
    contextMenuTriggeredByRightClick.value = options.source === 'right-click'

    // Check if there's a locked shape at the pointer position.
    // This handles cases where:
    // 1. No shapes are selected (clicking on empty canvas with locked shape)
    // 2. A locked shape is on top of an unlocked shape (the unlocked shape gets selected by tldraw,
    //    but we want to show context menu for the locked shape)
    const longPressedShape = getLongPressedShape(e)
    if (longPressedShape?.isLocked === true) {
      selectedShapes.value = [longPressedShape]
    } else if (selectedShapes.value.length === 0 && longPressedShape != null) {
      selectedShapes.value = [longPressedShape]
    }

    contextMenuAnchor.value = pointerEventCoords
    showContextMenu()

    // When there's no selected shapes, we show a default context menu with some useful options.
    const hasSelectedShapes = selectedShapes.value.length > 0
    if (!hasSelectedShapes) {
      const sharingAndBackgroundGroup = [
        sharingAndPermissionsRowComputed.value,
        changeBackgroundRowComputed.value,
      ].filter((item) => item != null && item.shouldShow)

      const pasteAndSelectAllGroup = whiteboardStore.selectedToolId === 'select' ? [[pasteRow], [selectAllRow]] : []

      contextMenuGroups.value = [sharingAndBackgroundGroup, ...pasteAndSelectAllGroup].filter(
        (group) => !isEmpty(group),
      ) as OzMenuRowsRow[][]
      return
    }

    // When all shapes are frozen, we only show "Unfreeze" option.
    const areAllSelectedShapesFrozen = selectedShapes.value.every((shape) => shape.isLocked)
    if (areAllSelectedShapesFrozen) {
      contextMenuGroups.value = [[unfreezeRow]]
      return
    }

    if (!areAllSelectedShapesEditable.value) {
      // If the selected shapes are not allowed to be created anymore (Allowed tools settings),
      // we hide "Copy" and "Duplicate" options.
      if (!areSelectedShapesTypesAllowedToBeCreated.value) {
        contextMenuGroups.value = [[pasteRow, selectAllRow]]
        return
      }
      contextMenuGroups.value = [[copyRow, pasteRow, duplicateRow, selectAllRow]]
      return
    }

    // Show only "delete" if any of the selected shapes are being uploaded - user should not be able to edit them
    const isSelectedShapeUploading = selectedShapes.value.some((shape) =>
      Object.values(whiteboardUploadStore.uploads).some((upload) => upload.shapeId === shape.id),
    )
    if (isSelectedShapeUploading) {
      contextMenuGroups.value = [[deleteRow]]
      return
    }

    contextMenuGroups.value = [
      [
        { ...editPollRow, shouldShow: isOnlySelectedShapePoll.value },
        { ...openPollRow, shouldShow: isOnlySelectedShapePoll.value && isOnlySelectedShapePollClosed.value },
        { ...closePollRow, shouldShow: isOnlySelectedShapePoll.value && !isOnlySelectedShapePollClosed.value },
        { ...exportPollRow, shouldShow: isOnlySelectedShapePoll.value && !device.app },
      ].filter((item) => item.shouldShow),
      [
        { ...sendToFrontRow, shouldShow: true },
        { ...sendToBackRow, shouldShow: true },
        {
          ...groupRow,
          shouldShow: shouldEnableGroupingForShapes(selectedShapes.value) && selectedShapes.value.length > 1,
        },
        {
          ...ungroupRow,
          shouldShow:
            shouldEnableGroupingForShapes(selectedShapes.value) &&
            selectedShapes.value.length === 1 &&
            isGroupShape(selectedShapes.value[0]),
        },
      ].filter((item) => item.shouldShow),
      [linkRow.value, specialEffectsRow.value].filter((item) => item.shouldShow),
      [
        // If the user can edit shapes but they can't add new shapes of this type,
        // we hide "Cut", "Copy" and "Duplicate".
        { ...cutRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
        { ...copyRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
        { ...pasteRow, shouldShow: true },
        { ...duplicateRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
      ].filter((row) => row.shouldShow),
      [selectAllRow],
      [
        {
          ...freezeRow,
          shouldShow: !isShapeChildOfGroup(onlySelectedShape.value as TLShape),
        },
        { ...deleteRow, shouldShow: true },
      ].filter((item) => item.shouldShow),
    ].filter((group) => !isEmpty(group)) as OzMenuRowsRow[][]
  }

  // When the selection changes, we want to hide the context menu. This solves 2 real-time problems:
  // 1. The context menu doesn't match the actual selected shape(s)' position due to the shape(s) being moved by other users.
  // 2. The context menu still shows for shape(s) that were deleted by other users.
  const currentSelectionPageBounds = shallowRef<Box | null>(null)
  useTldrawEditor().react((editor) => {
    const newSelectionPageBounds = editor.getSelectionPageBounds()
    const boundsChanged =
      currentSelectionPageBounds.value?.x !== newSelectionPageBounds?.x ||
      currentSelectionPageBounds.value?.y !== newSelectionPageBounds?.y ||
      currentSelectionPageBounds.value?.width !== newSelectionPageBounds?.width ||
      currentSelectionPageBounds.value?.height !== newSelectionPageBounds?.height
    if (boundsChanged) {
      hideContextMenu()
    }
    currentSelectionPageBounds.value = newSelectionPageBounds
  })

  // #endregion

  // #region Element modifier menu

  const elementModifierShapeTool = computed<WhiteboardTool | null>(() => {
    if (onlySelectedShape.value?.typeName !== 'shape') {
      return null
    }
    // Find the tool that matches the selected shape
    const tool = getAllTools().find((tool) => tool.tldrawToolId === onlySelectedShape.value?.type)
    if (tool == null) {
      return null
    }
    return {
      ...tool,
    }
  })

  const selectedShapeColor = computed(() => {
    const currentShapeColor: TldrawDefaultColorStyleColor | undefined = (onlySelectedShape.value?.props as any)?.color
    if (currentShapeColor == null) {
      return undefined
    }
    if (
      onlySelectedShape.value?.type === 'geo' ||
      onlySelectedShape.value?.type === 'arrow' ||
      onlySelectedShape.value?.type === 'padlet-speech-bubble' ||
      onlySelectedShape.value?.type === 'padlet-ooxml-shape'
    ) {
      return onlySelectedShape.value?.type === 'geo' ||
        onlySelectedShape.value?.type === 'padlet-speech-bubble' ||
        onlySelectedShape.value?.type === 'padlet-ooxml-shape'
        ? mapTldrawColorToWhiteboardColor(geoToolColors, currentShapeColor)
        : mapTldrawColorToWhiteboardColor(arrowToolColors, currentShapeColor)
    } else if (onlySelectedShape.value?.type !== 'geo') {
      const secondaryMenuItems = elementModifierShapeTool.value?.secondaryMenu?.items ?? []
      const colorClass = mapTldrawColorToWhiteboardColor(secondaryMenuItems, currentShapeColor) as
        | ToolTailwindColorType
        | undefined

      if (
        isAppUsing('sandboxTextFormatting') &&
        onlySelectedShape.value?.type === 'text' &&
        'richText' in onlySelectedShape.value.props
      ) {
        // If the color of the first character is set, display that color in the element modifier menu.
        const firstCharacterTextColorClass = getFirstCharacterTextColorClass(onlySelectedShape.value)
        return firstCharacterTextColorClass ?? colorClass
      }
      return colorClass
    }
    return undefined
  })

  const selectedShapeFillStyle = computed(() => {
    const isGeoShape = onlySelectedShape.value?.type === 'geo'
    const isPadletSpeechBubble = onlySelectedShape.value?.type === 'padlet-speech-bubble'
    if (!isGeoShape && !isPadletSpeechBubble) {
      return undefined
    }
    const currentShapeFillStyle: 'none' | 'semi' | 'solid' | undefined = (onlySelectedShape.value?.props as any)?.fill
    return currentShapeFillStyle
  })

  const selectedShapeGeoStyle = computed(() => {
    const isGeoShape = onlySelectedShape.value?.type === 'geo'
    if (!isGeoShape) {
      return undefined
    }
    const currentShapeGeoStyle: TldrawGeoShapeGeoStyle | undefined = (onlySelectedShape.value?.props as any)?.geo
    return currentShapeGeoStyle
  })

  const allSelectedShapes = computed(() => {
    return selectedShapes.value.flatMap((shape) => (isGroupShape(shape) ? getAllShapesInGroup(shape) : [shape]))
  })

  const isMultipleSelectedShapes = computed<boolean>(() => {
    return allSelectedShapes.value.length > 1
  })

  const selectedMultipleShapesSize = computed<WhiteboardTextSize | null>(() => {
    if (allSelectedShapes.value.every((shape) => shape.meta.autoSize === true)) return 'auto'
    if (allSelectedShapes.value.some((shape) => shape.meta.autoSize === true)) return null
    return getCommonShapeProperty({
      shapes: allSelectedShapes.value,
      property: 'size',
    })
  })

  const selectedShapeSize = computed<WhiteboardTextSize | null>(() => {
    if (onlySelectedShape.value == null) return null
    if ('size' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.size
    }

    if (isGroupShape(onlySelectedShape.value)) {
      const allShapesInGroup = getAllShapesInGroup(onlySelectedShape.value)
      return getCommonShapeProperty({
        shapes: allShapesInGroup,
        property: 'size',
      })
    }

    return null
  })

  const selectedShapeScale = computed(() => {
    if (onlySelectedShape.value != null && 'scale' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.scale
    }
    return null
  })

  const isSelectedShapeAutoSize = computed(() => {
    return (
      (onlySelectedShape.value?.type !== 'note' &&
        selectedShapeScale.value != null &&
        selectedShapeScale.value !== 1) ||
      (onlySelectedShape.value?.meta?.autoSize as boolean)
    )
  })

  const selectedSize = computed<WhiteboardTextSize | null>(() => {
    if (isMultipleSelectedShapes.value) return selectedMultipleShapesSize.value
    return isSelectedShapeAutoSize.value ? 'auto' : selectedShapeSize.value
  })

  const selectedShapeFontName = computed(() => {
    if (onlySelectedShape.value == null) return null
    if (onlySelectedShape.value.meta.useThemeFont === true) {
      return THEME_FONT_MENU_ITEM_ID
    }
    if ('font' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.font
    }

    if (isGroupShape(onlySelectedShape.value)) {
      return getCommonShapeProperty<TLDefaultFontStyle>({
        shapes: getAllShapesInGroup(onlySelectedShape.value),
        property: 'font',
      })
    }
    return null
  })

  const selectedMultipleShapesFontName = computed(() => {
    const allShapesUsingThemeFont = allSelectedShapes.value.every((shape) => shape.meta.useThemeFont)
    if (allShapesUsingThemeFont) {
      return THEME_FONT_MENU_ITEM_ID
    }
    return getCommonShapeProperty<TLDefaultFontStyle>({
      shapes: allSelectedShapes.value,
      property: 'font',
    })
  })

  const selectedFontName = computed(() => {
    return isMultipleSelectedShapes.value ? selectedMultipleShapesFontName.value : selectedShapeFontName.value
  })

  const selectedShapeConnectorStyle = computed<ConnectorStyle | undefined>(() => {
    const isArrowShape = onlySelectedShape.value?.type === 'arrow'
    if (!isArrowShape) {
      return undefined
    }
    const currentShapeArrowheadStartStyle: TLArrowShapeArrowheadStyle = (onlySelectedShape.value?.props as any)
      ?.arrowheadStart
    const currentShapeArrowheadEndStyle: TLArrowShapeArrowheadStyle = (onlySelectedShape.value?.props as any)
      ?.arrowheadEnd
    const currentShapeDashStyle: TLDefaultDashStyle = (onlySelectedShape.value?.props as any)?.dash
    return [currentShapeArrowheadStartStyle, currentShapeDashStyle, currentShapeArrowheadEndStyle]
  })

  const nonEmptyTextShapes = computed(() => {
    // Filter out empty text shapes to allow formatting operations (bold, italic, strikethrough) on non-empty text shapes within a group selection
    return allSelectedShapes.value?.filter((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return !useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isEmpty
    })
  })

  const isAllSelectedShapesTextBold = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive('bold')
    })
  })

  const isAllSelectedShapesTextItalic = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'italic',
      )
    })
  })

  const isAllSelectedShapesTextStrikethrough = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'strike',
      )
    })
  })

  const isAllSelectedShapesBulletedList = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'bulletList',
      )
    })
  })

  const isAllSelectedShapesNumberedList = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'orderedList',
      )
    })
  })

  /**
   * Determines the common text alignment for all selected text shapes.
   *
   * Checks if all non-empty text shapes in the current selection have the same
   * text alignment (start/left, middle/center, or end/right). This is used to
   * determine whether to show a unified alignment state in the UI.
   *
   * @returns {TLDefaultTextAlignStyle | null} The common alignment if all shapes
   *   are aligned the same way ('start', 'middle', 'end'), or null if shapes
   *   have different alignments or no text shapes are selected
   * @see https://github.com/tldraw/tldraw/pull/3627 - Text uses `textAlign` property, and geo/notes use `align` property
   */
  const isAllSelectedShapesAligned = computed((): TLDefaultTextAlignStyle | null => {
    if (nonEmptyTextShapes.value.length === 0) return null

    // Get the first shape's alignment as reference
    const firstShape = nonEmptyTextShapes.value[0]
    let firstAlignment: 'start' | 'middle' | 'end' | null = null

    if (firstShape.type === 'text' && 'textAlign' in firstShape.props) {
      firstAlignment = firstShape.props.textAlign
    } else if ('align' in firstShape.props) {
      firstAlignment = firstShape.props.align as 'start' | 'middle' | 'end'
    }

    if (firstAlignment === null) return null

    // @see https://github.com/tldraw/tldraw/pull/3627 - Text uses `textAlign` property, and geo/notes use `align` property
    const allSameAlignment = nonEmptyTextShapes.value.every((shape) => {
      if (shape.type === 'text' && 'textAlign' in shape.props) {
        return shape.props.textAlign === firstAlignment
      } else if ('align' in shape.props) {
        return shape.props.align === firstAlignment
      }
      return false
    })

    return allSameAlignment ? firstAlignment : null
  })

  /**
   * Gets the appropriate icon name for the text alignment button based on
   * the current alignment state of selected shapes.
   *
   * Icon names are named `align_left`, `align_center`, and `align_right` in the Google bucket.
   *
   */
  const textAlignmentIconName = computed((): 'align_left' | 'align_center' | 'align_right' => {
    const currentAlignment = isAllSelectedShapesAligned.value

    switch (currentAlignment) {
      case 'start':
        return 'align_left'
      case 'middle':
        return 'align_center'
      case 'end':
        return 'align_right'
      default:
        return 'align_left' // Default to left alignment
    }
  })

  const textFormattingItems = computed<WhiteboardTool[]>(() => {
    // Get the dynamic icon name based on current alignment
    const alignmentIconName = textAlignmentIconName.value
    const shouldShowTextAlignment =
      elementModifierShapeTool.value?.tldrawToolId !== 'geo' &&
      elementModifierShapeTool.value?.tldrawToolId !== 'padlet-speech-bubble' &&
      elementModifierShapeTool.value?.tldrawToolId !== 'padlet-ooxml-shape'

    return [
      {
        ...textFormattingOptions[TextFormattingOption.Bold],
        isSelected: isAllSelectedShapesTextBold.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.Italic],
        isSelected: isAllSelectedShapesTextItalic.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.Strikethrough],
        isSelected: isAllSelectedShapesTextStrikethrough.value,
        xDivider: true,
      },
      // Add text alignment if feature flag is enabled AND not a geo shape
      shouldShowTextAlignment && {
        ...textFormattingOptions[TextFormattingOption.TextAlignment],
        iconName: alignmentIconName,
        xDivider: true,
      },
      {
        ...textFormattingOptions[TextFormattingOption.BulletedList],
        isSelected: isAllSelectedShapesBulletedList.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.NumberedList],
        isSelected: isAllSelectedShapesNumberedList.value,
        xDivider: true,
      },
    ].filter(Boolean) as WhiteboardTool[]
  })

  // #endregion

  /**
   * Checks if `pointerdown` event should show content moderation UI for
   * locked unapproved shapes. We need to handle this case manually because
   * Tldraw doesn't include locked shapes in `getSelectedShapes()` results.
   */
  function checkAndShowContentModerationModifier(e: PointerEvent): void {
    const editor = useTldrawEditor().editor
    if (!editor) return

    // Find the shape that was clicked on
    const zoomLevel = editor.getZoomLevel()
    const shapeAtPoint = editor.getShapeAtPoint(editor.inputs.currentPagePoint, {
      hitLocked: true,
      margin: defaultTldrawOptions.hitTestMargin / zoomLevel,
      renderingOnly: true,
    })

    // If found a shape and it is locked and unapproved, select it
    // to show content moderation UI
    if (shapeAtPoint?.isLocked === true && !isShapeApproved(shapeAtPoint)) {
      e.stopPropagation()
      selectedShapes.value = [shapeAtPoint]
      return
    }

    // Otherwise, if the only selected shape is locked and unapproved,
    // clear the selection to hide the content moderation UI
    if (onlySelectedShape.value?.isLocked === true && !isShapeApproved(onlySelectedShape.value)) {
      e.stopPropagation()
      // Don't clear selection immediately in this rendering cycle.
      // The user might be trying to click on Approve/Reject buttons
      // so if we clear selection immediately, the buttons won't be clickable.
      void awaitDOMUpdates().then(() => {
        selectedShapes.value = []
      })
    }
  }

  return {
    onlySelectedShape,
    selectedShapes,
    areSelectedShapesTypesAllowedToBeCreated,

    // Context menu
    xContextMenu,
    contextMenuGroups,
    contextMenuAnchor,
    contextMenuTriggeredByRightClick,
    isOnlySelectedShapePollClosed,
    isOnlySelectedShapePoll,
    showContextMenu,
    hideContextMenu,
    buildContextMenu,

    // Element modifier menu
    elementModifierShapeTool,
    textAlignmentIconName,
    selectedShapeColor,
    selectedShapeFillStyle,
    selectedShapeGeoStyle,
    allSelectedShapes,
    isMultipleSelectedShapes,
    selectedMultipleShapesSize,
    selectedSize,
    selectedFontName,
    selectedShapeConnectorStyle,
    isAllSelectedShapesTextBold,
    isAllSelectedShapesTextItalic,
    isAllSelectedShapesTextStrikethrough,
    isAllSelectedShapesAligned,
    textFormattingItems,
    checkAndShowContentModerationModifier,
  }
})
